import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { CountryFlag } from '../CountryFlag';
import { VideoPlayer } from '../VideoPlayer/VideoPlayer';
import { formatPrice } from '../../utilities/formatPrice';
import { placeholderImage } from '../../utilities/images';
import { userPlaceholderImage } from '../../utilities/random';
import { apiClient } from '../../utilities/apiClient';
import { getUserDetails } from '../../containers/PostDetailPage/saga/saga';

/**
 * Instagram-feed-style post card. Single component used by both:
 *   - HomePage feed (M5 swapped in over the legacy PostHomeCard)
 *   - MyPostsPage  (M5 swapped in over the previous horizontal PostCard)
 *
 * Owner-aware action: when `post.userId === currentUserId` the action
 * icon is an edit pencil that navigates to /edit/:postId; otherwise
 * it's a chat bubble that POSTs /conversations + navigates to the new
 * chat. Hidden entirely if `post.userId` is missing.
 *
 * Seller name + avatar are hydrated per card via getUserDetails — N+1
 * per feed page; acceptable at MVP scale, future PR can denormalise
 * the seller into the list response.
 */
export const PostCard = ({ post, currentUserId }) => {
  const history = useHistory();
  const intl = useIntl();
  const [seller, setSeller] = useState(null);

  const id = post?.id;
  const sellerId = post?.userId;
  const isOwner = Boolean(currentUserId && sellerId && currentUserId === sellerId);
  const heroImage = post?.images?.[0];
  const heroVideo = post?.videos?.[0];
  const imageCount = post?.images?.length ?? 0;
  const hasVideo = Boolean(post?.videos?.length);
  const priceText = formatPrice(post?.price, post?.currencyCode);
  const timeAgo = useMemo(() => relativeTime(post?.createdAt, intl), [post?.createdAt, intl]);

  const chatLabel = intl.formatMessage({ id: 'postCard.aria.chat', defaultMessage: 'Message seller' });
  const editLabel = intl.formatMessage({ id: 'postCard.aria.edit', defaultMessage: 'Edit listing' });
  const openLabel = intl.formatMessage({ id: 'postCard.aria.openPost', defaultMessage: 'Open listing' });
  const fallbackName = intl.formatMessage({ id: 'chat.fallback.dresezUser', defaultMessage: 'dresez user' });

  // Hydrate seller per card. Anonymous sellers 404 on the public endpoint
  // — render with placeholder name/avatar in that case so the card still
  // shows the listing cleanly.
  useEffect(() => {
    if (!sellerId) return;
    let cancelled = false;
    getUserDetails({ userId: sellerId })
      .then((u) => {
        if (!cancelled && u) setSeller(u);
      })
      .catch(() => null);
    return () => {
      cancelled = true;
    };
  }, [sellerId]);

  const openPost = () => {
    if (id) history.push(`/post/${id}`);
  };

  const openSellerProfile = (e) => {
    e.stopPropagation();
    if (sellerId) history.push(`/user/${sellerId}`);
  };

  const onEdit = (e) => {
    e.stopPropagation();
    if (id) history.push(`/edit/${id}`);
  };

  const onChat = async (e) => {
    e.stopPropagation();
    if (!sellerId) return;
    try {
      const res = await apiClient.post('/conversations', {
        receiverId: sellerId,
        ...(id ? { postId: id } : {}),
      });
      const chatId = res?.data?.conversation?.id;
      if (chatId) history.push(`/chats/${chatId}`);
    } catch {
      // Surfaced via the snackbar interceptor; swallow here so the
      // click handler doesn't reject visibly.
    }
  };

  // Meta location: comma-separate the populated parts so missing values
  // collapse cleanly without leaving stray commas.
  const locationParts = [post?.city, post?.region].filter(Boolean);
  const locationText = locationParts.join(', ');

  if (!post) return null;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={openLabel}
      onClick={openPost}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') openPost();
      }}
      className="
        w-full max-w-[520px] mx-auto bg-white dark:bg-[#17181c]
        border border-gray-200 dark:border-[#2a2c30] rounded-lg overflow-hidden
        cursor-pointer mb-4
        focus:outline-none focus:ring-2 focus:ring-[#3a6df0]
      "
    >
      {/* Header: avatar + name. Tap → seller profile. */}
      <div className="flex items-center gap-3 px-3 py-2 border-b border-gray-100 dark:border-[#2a2c30]">
        <button
          type="button"
          onClick={openSellerProfile}
          className="flex items-center gap-3 flex-1 min-w-0 text-left"
        >
          <img
            src={seller?.userImage || userPlaceholderImage}
            alt=""
            className="w-9 h-9 rounded-full object-cover bg-gray-200"
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-black dark:text-white truncate">
              {seller?.name || fallbackName}
            </div>
            {locationText && (
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {locationText}
              </div>
            )}
          </div>
        </button>
      </div>

      {/* Media */}
      <div
        className="relative w-full bg-gray-100 dark:bg-[#23252b]"
        style={{ aspectRatio: '4 / 5' }}
      >
        {heroVideo ? (
          <VideoPlayer src={heroVideo} className="w-full h-full" objectFit="cover" />
        ) : (
          <img
            src={heroImage || placeholderImage}
            alt={post?.title || ''}
            className="w-full h-full object-cover"
          />
        )}
        {hasVideo && !heroVideo && (
          <span className="absolute top-2 left-2 bg-black/60 text-white text-xs rounded px-1.5 py-0.5">
            🎥
          </span>
        )}
        {imageCount > 1 && (
          <span className="absolute top-2 right-2 bg-black/60 text-white text-xs rounded px-1.5 py-0.5">
            1 / {imageCount}
          </span>
        )}
      </div>

      {/* Action row */}
      <div className="flex items-center gap-2 px-3 py-2">
        {sellerId && (
          isOwner ? (
            <button
              type="button"
              onClick={onEdit}
              aria-label={editLabel}
              className="
                w-10 h-10 flex items-center justify-center rounded-full
                hover:bg-gray-100 dark:hover:bg-[#23252b] text-black dark:text-white
              "
            >
              <EditOutlinedIcon />
            </button>
          ) : (
            <button
              type="button"
              onClick={onChat}
              aria-label={chatLabel}
              className="
                w-10 h-10 flex items-center justify-center rounded-full
                hover:bg-gray-100 dark:hover:bg-[#23252b] text-black dark:text-white
              "
            >
              <ChatBubbleOutlineIcon />
            </button>
          )
        )}
      </div>

      {/* Caption: title (bold) + description excerpt. */}
      <div className="px-3 pb-2">
        {post?.title && (
          <div className="text-base font-semibold text-black dark:text-white">
            <span className="font-semibold mr-2">{seller?.name || fallbackName}</span>
            {post.title}
          </div>
        )}
        {post?.description && (
          <div className="text-sm text-gray-700 dark:text-gray-300 line-clamp-1">
            {post.description}
          </div>
        )}
      </div>

      {/* Meta row: price · condition · rounded flag · time */}
      <div className="px-3 pb-3 flex items-center flex-wrap gap-x-2 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
        {priceText && (
          <span className="font-semibold text-sm text-black dark:text-white">
            {priceText}
          </span>
        )}
        {post?.condition && <span>{post.condition}</span>}
        {post?.gender && <span>{post.gender}</span>}
        {post?.country && <CountryFlag code={post.country} size={18} rounded />}
        {timeAgo && <span className="ml-auto">{timeAgo}</span>}
      </div>
    </div>
  );
};

// Lightweight relative-time helper — avoids pulling in date-fns just
// for "5h ago". Returns null when the input is unparseable.
function relativeTime(isoOrDate, intl) {
  if (!isoOrDate) return null;
  const t = typeof isoOrDate === 'string' ? Date.parse(isoOrDate) : new Date(isoOrDate).getTime();
  if (!Number.isFinite(t)) return null;
  const diffMs = Date.now() - t;
  if (diffMs < 60_000) {
    return intl.formatMessage({ id: 'postCard.timeAgo.justNow', defaultMessage: 'just now' });
  }
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) return ago(intl, minutes, 'minute');
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return ago(intl, hours, 'hour');
  const days = Math.floor(hours / 24);
  if (days < 30) return ago(intl, days, 'day');
  const months = Math.floor(days / 30);
  if (months < 12) return ago(intl, months, 'month');
  const years = Math.floor(days / 365);
  return ago(intl, years, 'year');
}

function ago(intl, value, unit) {
  return intl.formatMessage(
    { id: 'postCard.timeAgo.relative', defaultMessage: '{value} {unit} ago' },
    { value, unit },
  );
}

export default PostCard;
